import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ASSET_TYPE_ID } from "../const";

export interface DeletedAsset {
  assetVersionId: number;
  pageVersionId: number;
  assetTypeId?: ASSET_TYPE_ID;
}

interface DeletedAssetTrackerState {
  deletedAssets: DeletedAsset[];
}

interface DeletedAssetTrackerActions {
  trackDeletedAsset: (assetVersionId: number, pageVersionId: number, assetTypeId?: ASSET_TYPE_ID) => void;
  trackDeletedAssets: (assets: DeletedAsset[]) => void;
  isAssetTracked: (assetVersionId: number) => boolean;
  consumeDeletedAssets: () => DeletedAsset[];
  clearDeletedAssets: () => void;
}

type DeletedAssetTrackerStore = DeletedAssetTrackerState & DeletedAssetTrackerActions;

const initialState: DeletedAssetTrackerState = {
  deletedAssets: [],
};

// Tracks replaced assets so they can be cleaned up on save
export const useDeletedAssetTrackerStore = create<DeletedAssetTrackerStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      trackDeletedAsset: (assetVersionId, pageVersionId, assetTypeId) => {
        if (!assetVersionId || Number.isNaN(assetVersionId) || assetVersionId <= 0) {
          console.warn("[DeletedAssetTracker] Invalid assetVersionId:", assetVersionId);
          return;
        }
        if (!pageVersionId || Number.isNaN(pageVersionId) || pageVersionId <= 0) {
          console.warn("[DeletedAssetTracker] Invalid pageVersionId:", pageVersionId);
          return;
        }

        const { deletedAssets } = get();
        const alreadyTracked = deletedAssets.some((a) => a.assetVersionId === assetVersionId);

        if (!alreadyTracked) {
          set(
            {
              deletedAssets: [...deletedAssets, { assetVersionId, pageVersionId, assetTypeId }],
            },
            false,
            "trackDeletedAsset",
          );
        }
      },

      trackDeletedAssets: (assets) => {
        const { deletedAssets } = get();
        const validAssets = assets.filter(
          (asset) =>
            asset.assetVersionId > 0 &&
            asset.pageVersionId > 0 &&
            !deletedAssets.some((existing) => existing.assetVersionId === asset.assetVersionId),
        );

        if (validAssets.length > 0) {
          set(
            {
              deletedAssets: [...deletedAssets, ...validAssets],
            },
            false,
            "trackDeletedAssets",
          );
        }
      },

      isAssetTracked: (assetVersionId) => {
        const { deletedAssets } = get();
        return deletedAssets.some((a) => a.assetVersionId === assetVersionId);
      },

      consumeDeletedAssets: () => {
        const { deletedAssets } = get();
        set({ deletedAssets: [] }, false, "consumeDeletedAssets");
        return deletedAssets;
      },

      clearDeletedAssets: () => {
        set({ deletedAssets: [] }, false, "clearDeletedAssets");
      },
    }),
    {
      name: "DeletedAssetTrackerStore",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);

// Convenient selectors
export const deletedAssetTrackerSelectors = {
  getDeletedAssets: (state: DeletedAssetTrackerStore) => state.deletedAssets,
  getDeletedAssetCount: (state: DeletedAssetTrackerStore) => state.deletedAssets.length,
};
